import { Box, List, ListItem, Typography } from '@material-ui/core';
import { Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ActionModal from '../../../components/controller-modal';
import { deleteController } from '../../../service/controllersApi';
import { API_REQUEST_ERROR_MESSAGE } from '../../../utility/constants';
import useStyles from './styles';
import { useHistory } from 'react-router';

/**
 * @param {object} props - The `ControllerParametersModal` component accepts the following props:
 * @property {boolean} isOpen - Determines if the modal is visible.
 * @property {function} onClose - Callback function triggered when the modal is closed.
 * @property {number} controllerId - Username for the controller, displayed as a parameter.
 * @property {function} showToaster - Toaster function triggered after the API call.
 * @property {string} name - Controller name.
 * @returns {JSX.Element|null} The `ControllerParametersModal` component renders a modal displaying the controller's connection parameters,
 * including a QR code, URL, port, username, and password. If `isOpen` is false, the component renders `null`.
 */
const ControllerRemoveModal = ({ 
  isOpen, 
  onClose, 
  controllerId, 
  showToaster,
  name,
  showLoading
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await deleteController(controllerId);
  
      history.push('/controllers');
      onClose();
      showToaster(t('success'), t('controller-modal.remove-controller-success', { name }), 'success');
    } catch {
      showToaster(t('error'), t(API_REQUEST_ERROR_MESSAGE), 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('controller-modal.remove-controller-title', { name })}
      cancelButtonLabel={t('controller-modal.cancel-button')}
      submitButtonLabel={t('controller-modal.remove-controller-submit-button', { name })}
      handleSubmit={handleSubmit}
      buttonStyles={classes.button}
      buttonIcon={<Delete />}
    >
      { showLoading(isLoading) }
      <Box className={classes.content}>
        <Typography className={classes.contentHeader}>
          <Trans i18nKey={'controller-modal.remove-controller-description'} values={{ name }}/>
        </Typography>
        <Typography className={classes.warning}>
          <Trans i18nKey={'controller-modal.remove-controller-effects'}/>
        </Typography>
        <List className={classes.list}>
          <ListItem>{t('controller-modal.remove-controller-effect-remove-aps')}</ListItem>
          <ListItem>{t('controller-modal.remove-controller-effect-delete-permissions')}</ListItem>
          <ListItem>{t('controller-modal.remove-controller-effect-remove-from-server')}</ListItem>
        </List>
        <Typography className={classes.warning}>
          <Trans i18nKey={'controller-modal.remove-controller-confirmation-message'}/>
        </Typography>
      </Box>
    </ActionModal>
  );
};

ControllerRemoveModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  controllerId: PropTypes.number.isRequired,
  showToaster: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  showLoading: PropTypes.func.isRequired,
};

export default ControllerRemoveModal;